import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, PlusCircle, Copy, Trash2, Edit2, Loader2, Mail, List, LayoutGrid } from 'lucide-react';
import { useEmailTemplates } from '@/hooks/email/useEmailTemplates';
import { EmailTemplate, EmailTemplateInput } from '@/services/api/emailTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useEnhancedAuthContext } from '@/context/auth/EnhancedAuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EmailSignaturesPanel from '@/components/email/signatures/EmailSignaturesPanel';
import TemplateBodyEditor from '@/components/email/templates/TemplateBodyEditor';
import { htmlToText } from '@/utils/emailHtml';


const defaultTemplate = (): EmailTemplateInput => ({
  name: '',
  description: '',
  category: '',
  subject: '',
  body: '',
  is_active: true,
});

const EmailTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, isSuperuser } = useEnhancedAuthContext();
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [activeTab, setActiveTab] = useState<'templates' | 'signatures'>('templates');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<EmailTemplateInput>(defaultTemplate());
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);

  const canCreate = hasPermission('email_template.create');
  const canUpdate = hasPermission('email_template.update');
  const canDelete = hasPermission('email_template.delete');
  // Shared (global) templates are read-only for regular users; superusers manage the library.
  const canModify = (t: EmailTemplate) => !t.is_global || isSuperuser;

  const {
    templates,
    isLoading,
    refetch,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    isCreating,
    isUpdating,
    isDeleting,
  } = useEmailTemplates({ search });

  const filteredTemplates = React.useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.category || '').toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q)
    );
  }, [templates, search]);

  const handleCreateNew = useCallback(() => {
    setSelectedTemplate(null);
    setTemplateForm(defaultTemplate());
    setIsEditorOpen(true);
  }, []);

  const handleEdit = useCallback((template: EmailTemplate) => {
    if (template.is_global && !isSuperuser) return; // shared templates are read-only for regular users
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      category: template.category || '',
      subject: template.subject,
      body: template.body_html || template.body,
      is_active: template.is_active !== false,
    });
    setIsEditorOpen(true);
  }, [isSuperuser]);

  const handleDuplicate = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(null);
    setTemplateForm({
      name: `${template.name} (Copy)`,
      description: template.description || '',
      category: template.category || '',
      subject: template.subject,
      body: template.body_html || template.body,
      is_active: true,
    });
    setIsEditorOpen(true);
  }, []);

  const handleSave = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()) {
      return;
    }
    // The editor produces HTML: store it in body_html and a plain-text version in body.
    const html = templateForm.body;
    const payload: EmailTemplateInput = {
      ...templateForm,
      name: templateForm.name.trim(),
      category: templateForm.category?.trim() || undefined,
      body: htmlToText(html),
      body_html: html,
    };
    if (selectedTemplate) {
      await updateTemplate({ id: selectedTemplate.id, data: payload });
    } else {
      await createTemplate(payload);
    }
    setIsEditorOpen(false);
    setSelectedTemplate(null);
    setTemplateForm(defaultTemplate());
    refetch();
  };


  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteTemplate(deleteTarget.id);
    setDeleteTarget(null);
    refetch();
  };

  const updateField = (field: keyof EmailTemplateInput, value: string | boolean) => {
    setTemplateForm((prev) => ({ ...prev, [field]: value }));
  };

  const categories = React.useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t) => {
      if (t.category) set.add(t.category);
    });
    return Array.from(set).sort();
  }, [templates]);

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <p className="text-muted-foreground">
            Create and manage reusable email templates and signatures.
          </p>
        </div>
        {activeTab === 'templates' && (
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-muted rounded-md p-1 gap-1">
              <Button
                variant="ghost"
                size="sm"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-background text-foreground shadow-sm hover:bg-background' : 'text-muted-foreground'}
              >
                <List className="mr-2 h-4 w-4" />
                List
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-pressed={viewMode === 'card'}
                onClick={() => setViewMode('card')}
                className={viewMode === 'card' ? 'bg-background text-foreground shadow-sm hover:bg-background' : 'text-muted-foreground'}
              >
                <LayoutGrid className="mr-2 h-4 w-4" />
                Cards
              </Button>
            </div>

            {canCreate && (
              <Button onClick={handleCreateNew}>
                <PlusCircle className="mr-2 h-4 w-4" />
                New Template
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'templates' | 'signatures')}>
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="signatures">Signatures</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-6 pt-4">

      <div className="flex items-center gap-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates..."
          className="max-w-sm"
        />
        {categories.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Badge key={cat} variant="secondary" className="cursor-pointer" onClick={() => setSearch(cat)}>
                {cat}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {filteredTemplates.length === 0 ? (
        <div className="border-2 border-dashed border-muted rounded-lg p-12 text-center">
          <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No email templates found</h3>
          <p className="text-muted-foreground mb-6">
            {search.trim() ? 'Try a different search term.' : 'Create your first template to speed up outreach.'}
          </p>
          {canCreate && !search.trim() && (
            <Button onClick={handleCreateNew}>
              <PlusCircle className="mr-2 h-4 w-4" />
              New Template
            </Button>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{template.name}</span>
                      {template.is_global && (
                        <Badge variant="secondary">Shared</Badge>
                      )}
                      {template.is_active === false && (
                        <Badge variant="outline" className="text-muted-foreground">
                          Inactive
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {template.category ? (
                      <Badge variant="secondary">{template.category}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <p className="truncate text-sm">{template.subject}</p>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {canCreate && (
                        <Button variant="outline" size="sm" onClick={() => handleDuplicate(template)}>
                          <Copy className="h-4 w-4" />
                          <span className="sr-only">Duplicate</span>
                        </Button>
                      )}
                      {canDelete && canModify(template) && (
                        <Button variant="outline" size="sm" onClick={() => setDeleteTarget(template)}>
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      )}
                      {canUpdate && canModify(template) && (
                        <Button variant="default" size="sm" onClick={() => handleEdit(template)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle>{template.name}</CardTitle>
                  <div className="flex items-center gap-2">
                    {template.is_global && (
                      <Badge variant="secondary">Shared</Badge>
                    )}
                    {template.is_active === false && (
                      <Badge variant="outline" className="text-muted-foreground">
                        Inactive
                      </Badge>
                    )}
                  </div>
                </div>
                <CardDescription className="truncate">
                  {template.category ? (
                    <Badge variant="secondary" className="mr-2">
                      {template.category}
                    </Badge>
                  ) : null}
                  {template.description || 'No description'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md p-3 mb-4 overflow-hidden max-h-24 bg-muted/30">
                  <div className="text-sm text-muted-foreground">
                    <div className="font-medium truncate mb-1">{template.subject}</div>
                    <p className="truncate whitespace-pre-wrap line-clamp-3">{template.body}</p>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  {canCreate && (
                    <Button variant="outline" size="sm" onClick={() => handleDuplicate(template)}>
                      <Copy className="h-4 w-4" />
                      <span className="sr-only">Duplicate</span>
                    </Button>
                  )}
                  {canDelete && canModify(template) && (
                    <Button variant="outline" size="sm" onClick={() => setDeleteTarget(template)}>
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  )}
                  {canUpdate && canModify(template) && (
                    <Button variant="default" size="sm" onClick={() => handleEdit(template)}>
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="signatures" className="pt-4">
          <EmailSignaturesPanel
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
          />
        </TabsContent>
      </Tabs>

      {/* Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="max-w-4xl h-[92vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="text-base">{selectedTemplate ? 'Edit Email Template' : 'Create Email Template'}</DialogTitle>
            <DialogDescription className="text-xs">
              {selectedTemplate
                ? 'Update your reusable email template below.'
                : 'Build a new template for outreach. Use placeholders like <first_name>, <company_name>.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="template-name" className="text-xs">Template Name</Label>
                <Input
                  id="template-name"
                  className="h-9"
                  value={templateForm.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g. Free Trial Follow-up"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="template-category" className="text-xs">Category</Label>
                <Input
                  id="template-category"
                  className="h-9"
                  value={templateForm.category}
                  onChange={(e) => updateField('category', e.target.value)}
                  placeholder="e.g. Outreach"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="template-description" className="text-xs">Description</Label>
                <Input
                  id="template-description"
                  className="h-9"
                  value={templateForm.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Brief description"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="template-subject" className="text-xs">Subject Line</Label>
              <Input
                id="template-subject"
                className="h-9"
                value={templateForm.subject}
                onChange={(e) => updateField('subject', e.target.value)}
                placeholder="Email subject line"
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="template-body" className="text-xs">Body</Label>
                <span className="text-[11px] text-muted-foreground">
                  Placeholders: {'<first_name>'}, {'<company_name>'}, {'<position>'}
                </span>
              </div>
              <TemplateBodyEditor
                value={templateForm.body}
                onChange={(html) => updateField('body', html)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 px-6 py-3 border-t bg-background">
            <div className="flex items-center gap-2">
              <Switch
                id="template-active"
                checked={templateForm.is_active !== false}
                onCheckedChange={(checked) => updateField('is_active', checked)}
              />
              <Label htmlFor="template-active" className="cursor-pointer text-sm">
                Active
              </Label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsEditorOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={
                  isCreating ||
                  isUpdating ||
                  !templateForm.name.trim() ||
                  !templateForm.subject.trim() ||
                  !templateForm.body.trim()
                }
              >
                {isCreating || isUpdating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <SaveIcon className="h-4 w-4 mr-2" />
                )}
                Save Template
              </Button>
            </div>
          </div>

        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteTarget?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Inline icon component to avoid extra import churn
const SaveIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default EmailTemplatesPage;
